import { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Alert } from '@mui/material';
import {
  UserTable,
  RoleChangeModal,
  CreateUserModal,
} from '../../components/features/admin';
import { userService } from '../../services';
import type { User } from '../../types';

interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'user';
}

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role change modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; role: string } | null>(null);
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [roleChangeError, setRoleChangeError] = useState<string | null>(null);

  // Create user modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await userService.listUsers({
        page: page + 1,
        size: rowsPerPage,
      });
      setUsers(response.items);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || '사용자 목록을 불러오는데 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newSize: number) => {
    setRowsPerPage(newSize);
    setPage(0);
  };

  const handleRoleChangeClick = (userId: string, currentRole: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUser({ id: user.id, name: user.name, role: currentRole });
      setRoleModalOpen(true);
    }
  };

  const handleRoleChangeConfirm = async (userId: string, newRole: 'admin' | 'user') => {
    setRoleChangeLoading(true);
    setRoleChangeError(null);
    try {
      await userService.updateUserRole(userId, { role: newRole });
      setRoleModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setRoleChangeError(error.response?.data?.detail || '역할 변경에 실패했습니다');
    } finally {
      setRoleChangeLoading(false);
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      await userService.updateUserStatus(userId, { is_active: !currentStatus });
      fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || '상태 변경에 실패했습니다');
    }
  };

  const handleCreateUser = async (data: CreateUserData) => {
    setCreateLoading(true);
    setCreateError(null);
    try {
      await userService.createUser(data);
      setCreateModalOpen(false);
      fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setCreateError(error.response?.data?.detail || '사용자 생성에 실패했습니다');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        사용자 관리
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <UserTable
        users={users}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRoleChange={handleRoleChangeClick}
        onStatusToggle={handleStatusToggle}
        onCreateUser={() => setCreateModalOpen(true)}
        isLoading={isLoading}
      />

      <RoleChangeModal
        open={roleModalOpen}
        onClose={() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
          setRoleChangeError(null);
        }}
        user={selectedUser}
        onConfirm={handleRoleChangeConfirm}
        isLoading={roleChangeLoading}
        error={roleChangeError}
      />

      <CreateUserModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreateUser}
        isLoading={createLoading}
        error={createError}
      />
    </Container>
  );
}
